import { inject, Injectable, signal } from '@angular/core'
import { Router } from '@angular/router'
import { MenuController, ModalController } from '@ionic/angular/standalone'

import { environment } from '../../../environments/environment'
import { CasinoDisabledModalComponent } from '../components/casino-disabled-modal/casino-disabled-modal.component'
import { CasinoWalletEmptyModalComponent } from '../components/casino-wallet-empty-modal/casino-wallet-empty-modal.component'
import { AuthService } from './auth.service'
import { BalanceService } from './balance.service'
import { DomainService } from './domain.service'
import { HelperService } from './helper.service'
import { WhiteLabelMarketConfigService } from './white-label-market-config.service'

export type DisplayMode = 'time' | 'league'

@Injectable({
	providedIn: 'root',
})
export class DisplayService {
	private readonly STORAGE_KEY = 'display_mode'

	private readonly menuController = inject(MenuController)

	private readonly authService = inject(AuthService)

	private readonly helperService = inject(HelperService)

	private readonly domainService = inject(DomainService)

	private readonly wlMarketConfig = inject(WhiteLabelMarketConfigService)

	private readonly modalController = inject(ModalController)

	private readonly balanceService = inject(BalanceService)

	private readonly router = inject(Router)

	displayMode = signal<DisplayMode>('league')

	constructor() {
		const savedMode = localStorage.getItem(this.STORAGE_KEY) as DisplayMode
		if (savedMode && (savedMode === 'time' || savedMode === 'league')) {
			this.displayMode.set(savedMode)
		} else {
			// Set default to league if no saved preference
			localStorage.setItem(this.STORAGE_KEY, 'league')
		}
	}

	async setMode(mode: DisplayMode) {
		this.displayMode.set(mode)
		localStorage.setItem(this.STORAGE_KEY, mode)
		// Close the drawer when mode is changed
		await this.menuController.close()
	}

	get isCasinoBlocked(): boolean {
		return this.wlMarketConfig.showFeature('disableCasino') || this.authService.isCasinoBlocked$.getValue()
	}

	async openCasino() {
		const isWlDisabled = this.wlMarketConfig.showFeature('disableCasino')
		const userData = this.helperService.getUserData()
		const isUserBlocked = userData?.user_fields?.IS_BLOCKED_CASINO === 'true'
		const casinoWallet = this.balanceService.casinoBalanceData$.getValue()
		const casinoCurrency = casinoWallet?.CURRENCY_CODE ?? ''

		if (!casinoCurrency) return

		if (isWlDisabled || isUserBlocked) {
			const modal = await this.modalController.create({
				component: CasinoDisabledModalComponent,
				cssClass: 'casino-disabled-modal',
				breakpoints: [0, 1],
				initialBreakpoint: 1,
				handle: false,
				componentProps: {
					message: isUserBlocked ? 'Casino Blocked, contact your admin' : 'Casino Unavailable',
				},
			})
			await modal.present()
			return
		}

		if (!userData) {
			console.error('No user data available for casino')
			return
		}

		const casinoUrl = this.getCasinoUrl(casinoCurrency, userData)

		if (this.getWalletAvailable(casinoWallet) <= 0) {
			const modal = await this.modalController.create({
				component: CasinoWalletEmptyModalComponent,
				cssClass: 'casino-wallet-empty-modal',
				breakpoints: [0, 1],
				initialBreakpoint: 1,
				handle: false,
				componentProps: {
					casinoUrl,
				},
			})
			await modal.present()

			const { data } = await modal.onDidDismiss<{ action?: string }>()
			if (data?.action === 'transfer-wallet') {
				await this.router.navigate(['/wallet-transfer'])
			}
			return
		}

		window.open(casinoUrl, '_blank', 'noopener,noreferrer')
	}

	private getCasinoUrl(casinoCurrency: string, userData: NonNullable<ReturnType<HelperService['getUserData']>>): string {
		const base = environment.casinoUrl
		const logoutRedirectionUrl = this.domainService.hostname + '/home'
		const env = environment.environment?.toLowerCase()
		const userFields = userData.user_fields
		const userToken = this.authService.getAccessToken()
		const { ID, NAME } = userFields ?? {}

		const qs = new URLSearchParams({
			token: userToken,
			tokenTimestamp: String(Date.now()),
			userFields: JSON.stringify(userFields),
			userId: ID,
			currency: casinoCurrency,
			displayName: NAME,
			lang: this.helperService.getSelectedLanguage(),
			logoutRedirectionUrl,
			environment: env,
			origin: 'exchange',
		})

		return `${base}?${qs.toString()}`
	}

	private getWalletAvailable(wallet: { BALANCE?: string | number; TOTAL_EXPOSURE?: string | number } | null): number {
		if (!wallet) return 0
		return Number(wallet.BALANCE ?? 0) + Number(wallet.TOTAL_EXPOSURE ?? 0)
	}
}
